import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Container,
  Stack,
  ListItemText,
  Divider,
  CircularProgress
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

const menuItems = [
  { title: "Terms of Service", slug: "terms-of-service" },
  { title: "Privacy Policy", slug: "privacy-policy" },
  { title: "Cancellation and Refund Policy", slug: "refund-policy" },
  { title: "Cookie Policy", slug: "cookie-policy" },
  { title: "Discount & Coupons Policy", slug: "coupon-terms" },
  { title: "Grievance Policy", slug: "grievance-redressal" },
  { title: "Community Guidelines", slug: "community-guidelines" },
];

export default function About() {
  const navigate = useNavigate();

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [policiesData, setPoliciesData] = useState([]);
  const [loadingDoc, setLoadingDoc] = useState(false);

  // Fetch policies silently in background — no loading gate on the list
  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const response = await fetch(
          "https://editor.terms.mealno.in/api/collections/terms/records"
        );
        const json = await response.json();
        setPoliciesData(json.items || []);
      } catch (error) {
        console.error("Error fetching policies:", error);
      }
    };

    fetchPolicies();
  }, []);

  const handleOpenDoc = async (item) => {
    // Try to find already-fetched policy
    const match = policiesData.find((p) => p.slug === item.slug);

    if (match) {
      setSelectedDoc({ title: item.title, content: match.content });
      return;
    }

    // API still loading — fetch this single policy on demand
    setLoadingDoc(true);
    try {
      const response = await fetch(
        `https://editor.terms.mealno.in/api/collections/terms/records?filter=(slug='${item.slug}')`
      );
      const json = await response.json();
      const record = json.items?.[0];
      setSelectedDoc({
        title: item.title,
        content: record?.content || "<p>Policy content is currently unavailable.</p>",
      });
    } catch {
      setSelectedDoc({
        title: item.title,
        content: "<p>Policy content is currently unavailable.</p>",
      });
    } finally {
      setLoadingDoc(false);
    }
  };

  // Policy detail view
  if (loadingDoc) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress sx={{ color: "#F8D548" }} />
      </Box>
    );
  }

  if (selectedDoc) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", bgcolor: "#f8f8f8" }}>
        <Box
          sx={{
            background: "#F8D548",
            pb: 2, pt: 2, px: 2,
            display: "flex",
            alignItems: "center",
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          <Container maxWidth="sm" sx={{ display: "flex", alignItems: "center", p: 0 }}>
            <IconButton
              onClick={() => setSelectedDoc(null)}
              sx={{ color: "#4A332E", bgcolor: "rgba(255,255,255,0.15)", mr: 2 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight="800" color="#4A332E">
              {selectedDoc.title}
            </Typography>
          </Container>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: "auto", p: 3, bgcolor: "#fff" }}>
          <Container maxWidth="sm">
            <Box
              sx={{
                "& h1, & h2, & h3": { color: "#333", mt: 3, mb: 1 },
                "& p": { color: "#555", lineHeight: 1.6, mb: 2 },
                "& ul, & ol": { pl: 3, color: "#555", mb: 2 },
                "& li": { mb: 1 },
                "& strong": { fontWeight: 600 },
                "& a": { color: "#F8D548", textDecoration: "none" },
              }}
              dangerouslySetInnerHTML={{ __html: selectedDoc.content }}
            />
          </Container>
        </Box>
      </Box>
    );
  }

  // About list — always renders immediately, no loading gate
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8f8f8" }}>
      <Box sx={{ background: "#F8D548", borderRadius: "0 0 30px 30px", pb: 3, pt: 2 }}>
        <Container maxWidth="sm">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ color: "#4A332E", bgcolor: "rgba(255,255,255,0.15)" }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight="800" color="#4A332E">
              About
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 3,
            bgcolor: "#fff",
            borderRadius: "16px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
            border: "1px solid rgba(0,0,0,0.03)",
            overflow: "hidden",
          }}
        >
          <List disablePadding>
            {menuItems.map((item, index) => (
              <React.Fragment key={item.slug}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleOpenDoc(item)} sx={{ py: 2.5, px: 3 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="600" color="#222">
                          {item.title}
                        </Typography>
                      }
                    />
                    <ChevronRightIcon sx={{ color: "#ccc", fontSize: 22 }} />
                  </ListItemButton>
                </ListItem>
                {index < menuItems.length - 1 && (
                  <Divider sx={{ mx: 3, borderColor: "#f0f0f0" }} />
                )}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Container>
    </Box>
  );
}